<template>
  <FilterContainer>
    <template #filter>
      <label class="block">
        <span class="uppercase text-xs font-bold tracking-wide">{{
          `${filter.name} - ${__('From')}`
        }}</span>
        <div class="flex">
          <date-picker
            :clearable="true"
            :color="color"
            :format="format"
            :id="`${field.uniqueKey}-range-start`"
            :initial-value="startFieldValue"
            :input-attrs="{ style: 'direction: ltr; text-align: end;' }"
            inputClass="w-full form-control form-input form-input-bordered"
            :locale="locale"
            :max="max"
            :min="min"
            :placeholder="__('Start')"
            type="date"
            v-model="startValue"
            @input="handleStartDateChange"
            @selected="handleStartDateSelected"
          >
            <template #clear-btn="{ vm }">
              <svg role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 512"
                   class="ml-2" style="height: 16px; margin-top: 11px;">
                <path
                  fill="currentColor"
                  d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"></path>
              </svg>
            </template>
          </date-picker>
        </div>
      </label>

      <label class="block mt-2">
        <span class="uppercase text-xs font-bold tracking-wide">{{
          `${filter.name} - ${__('To')}`
        }}</span>
        <div class="flex">
          <date-picker
            :clearable="true"
            :color="color"
            :format="format"
            :id="`${field.uniqueKey}-range-end`"
            :initial-value="endFieldValue"
            :input-attrs="{ style: 'direction: ltr; text-align: end;' }"
            inputClass="w-full form-control form-input form-input-bordered"
            :locale="locale"
            :max="max"
            :min="min"
            :placeholder="__('End')"
            type="date"
            v-model="endValue"
            @input="handleEndDateChange"
            @selected="handleEndDateSelected"
          >
            <template #clear-btn="{ vm }">
              <svg role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 512"
                   class="ml-2" style="height: 16px; margin-top: 11px;">
                <path
                  fill="currentColor"
                  d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"></path>
              </svg>
            </template>
          </date-picker>
        </div>
      </label>
    </template>
  </FilterContainer>
</template>

<script>
import Vue3PersianDatetimePicker from 'vue3-persian-datetime-picker'
import debounce from 'lodash/debounce'
import jMoment from 'moment-jalaali'
import filled from '@/util/filled'

export default {
  emits: ['change'],

  components: {
    DatePicker: Vue3PersianDatetimePicker,
  },

  props: {
    resourceName: {
      type: String,
      required: true,
    },
    filterKey: {
      type: String,
      required: true,
    },
    lens: String,
  },

  data: () => ({
    startValue: null,
    endValue: null,
    debouncedHandleChange: null,
  }),

  created() {
    this.debouncedHandleChange = debounce(() => this.handleChange(), 500)
    this.setCurrentFilterValue()
  },

  mounted() {
    Nova.$on('filter-reset', this.handleFilterReset)
  },

  beforeUnmount() {
    Nova.$off('filter-reset', this.handleFilterReset)
  },

  watch: {
    startValue: {
      handler(newVal, oldVal) {
        // Only trigger if value actually changed and is not null
        if (newVal !== oldVal && newVal !== null && newVal !== '') {
          this.$nextTick(() => {
            this.handleChange()
          })
        }
      },
      immediate: false,
    },

    endValue: {
      handler(newVal, oldVal) {
        // Only trigger if value actually changed and is not null
        if (newVal !== oldVal && newVal !== null && newVal !== '') {
          this.$nextTick(() => {
            this.handleChange()
          })
        }
      },
      immediate: false,
    },
  },

  methods: {
    setCurrentFilterValue() {
      let [startValue, endValue] = this.filter.currentValue || [null, null]

      // Convert Gregorian dates to Persian for display
      this.startValue = filled(startValue)
        ? jMoment(startValue, 'YYYY-MM-DD').format(this.format)
        : null

      this.endValue = filled(endValue)
        ? jMoment(endValue, 'YYYY-MM-DD').format(this.format)
        : null
    },

    validateFilter(startValue, endValue) {
      // Convert Persian dates to Gregorian format (YYYY-MM-DD) for backend
      startValue = filled(startValue)
        ? jMoment(startValue, this.format).format('YYYY-MM-DD')
        : null

      endValue = filled(endValue)
        ? jMoment(endValue, this.format).format('YYYY-MM-DD')
        : null

      return [startValue, endValue]
    },

    handleStartDateChange(value) {
      // This will be handled by watch
    },

    handleEndDateChange(value) {
      // This will be handled by watch
    },

    handleStartDateSelected(value) {
      // When date is selected from calendar, update immediately without debounce
      this.startValue = value
      this.$nextTick(() => {
        this.handleChange()
      })
    },

    handleEndDateSelected(value) {
      // When date is selected from calendar, update immediately without debounce
      this.endValue = value
      this.$nextTick(() => {
        this.handleChange()
      })
    },

    handleChange() {
      this.$emit('change', {
        filterClass: this.filterKey,
        value: this.validateFilter(this.startValue, this.endValue),
      })
    },

    handleFilterReset() {
      this.setCurrentFilterValue()
    },
  },

  computed: {
    filter() {
      return this.$store.getters[`${this.resourceName}/getFilter`](
        this.filterKey
      )
    },

    field() {
      return this.filter.field
    },

    color() {
      return this.field.color || 'rgb(30, 136, 229)'
    },

    format() {
      return this.field.format || 'jYYYY/jMM/jDD'
    },

    locale() {
      return this.field.locale || 'fa,en'
    },

    min() {
      return this.field.min || '1300/01/01'
    },

    max() {
      return this.field.max || '1450/12/29'
    },

    startFieldValue() {
      return this.startValue
    },

    endFieldValue() {
      return this.endValue
    },
  },
}
</script>

<style>
.vpd-next svg,
.vpd-prev svg {
  display: inline;
}
</style>
